import React from 'react';
import {Redirect, Route, Switch} from 'react-router-dom';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Home from '../pages/Home';
import AdminRoutes from '../admin/AdminRoutes';
import Fund from '../pages/Fund';
import LoginCallback from '../pages/LoginCallback';
import webservice from "../common/Webservice";
import {dispatchUpdateData, formatCobiYear, isAllowed, mapMenu, parseAdminUrl, parseFundUrl, parseUrl} from '../misc/Shared';
import WebserviceDown from "../pages/WebserviceDown";

const propTypes = {
	currentCobiId: PropTypes.number,
	currentYear: PropTypes.number,
	currentFiscalYear: PropTypes.number,
	currentEditorFiscalYear: PropTypes.number,
	fiscalYears: PropTypes.array.isRequired,
	// The browser history object (provided by react router)
	history: PropTypes.object.isRequired,
	historyStack: PropTypes.array,
	userAccount: PropTypes.object.isRequired,
	admin: PropTypes.bool,
};

const defaultProps = {
	currentCobiId: undefined,
	currentYear: undefined,
};

const mapStateToProps = (state) => {
    return {
		currentCobiId: state.app.currentCobiId,
		currentFund: state.app.currentFund,
		currentYear: state.app.currentYear,
		currentFiscalYear: state.app.currentFiscalYear,
		currentEditorFiscalYear: state.app.currentEditorFiscalYear,
		fiscalYears: state.app.fiscalYears,
		historyStack: state.historyStack,
		userAccount: state.userAccount,
		admin: state.app.admin,
    };
};

class AppRoutes extends React.Component {
	constructor(props) {
		super(props);

        this.switchUrl(location.pathname);
		dispatchUpdateData([
			{field: 'historyStack', value: this.props.historyStack.concat([location.pathname])}
		]);

		this.componentDidUpdate(undefined);

        this.props.history.listen(location => {
			if (!this.props.historyStack.length || location.pathname !== this.props.historyStack[this.props.historyStack.length - 1]) {
				dispatchUpdateData([
					{field: 'historyStack', value: this.props.historyStack.concat([location.pathname])}
				]);
			}
        });
    }

    componentDidUpdate(prevProps) {
		this.switchUrl(this.props.history.location.pathname, prevProps);
	}

	switchUrl(url, prevProps) {
		if (url.startsWith('/fund')) {
			this.fundDispatch(url, prevProps);
		} else if (url.startsWith('/admin') && isAllowed()) {
			this.adminDispatch(url, prevProps);
		} else {
			this.dispatchUrlData(url, prevProps);
		}
	}

	fundDispatch(url, prevProps) {
		const defaultValues = {
			fundId: undefined
		};
		const urlParams = parseFundUrl(url, defaultValues);
		const defaultCobiId = this.props.currentCobiId === undefined ? 1 : this.props.currentCobiId;
		const defaultYear = this.props.currentYear === undefined ? this.props.currentFiscalYear : this.props.currentYear;

		dispatchUpdateData([
			{path: 'app', field: 'currentCobiId', value: defaultCobiId},
			{path: 'app', field: 'currentYear', value: defaultYear},
			{path: 'app', field: 'currentTab', value: 'overview'},
			{path: 'app', field: 'currentFund', value: urlParams.fundId},
			{path: 'app', field: 'admin', value: false},
		]);

		if (!prevProps || this.props.currentCobiId !== prevProps.currentCobiId ) {
			this.getMenu({cobiId: defaultCobiId, year: defaultYear});
		}
	}

	adminDispatch(url, prevProps) {
		const defaultValue = {
			years: this.props.fiscalYears.map(fiscalYearObject=> fiscalYearObject.sessionFY),
			currentEditorFiscalYear: this.props.currentEditorFiscalYear,
			cobiId: 1,
			tab: 'overview'
		};
		const urlParams = parseAdminUrl(url, defaultValue);
		const currentYear = this.props.currentYear;

		dispatchUpdateData([
			{path: 'app', field: 'currentYear', value: urlParams.year},
			{path: 'app', field: 'currentCobiId', value: urlParams.cobiId},
			{path: 'app', field: 'currentTab', value: urlParams.tab},
			{path: 'app', field: 'admin', value: true},
		]);

		if (!prevProps || this.props.currentCobiId !== prevProps.currentCobiId || currentYear !== prevProps.currentYear) {
			this.getAdminMenu(urlParams);
		}
	}

    dispatchUrlData(url, prevProps) {
		const defaultValues = {
            years: this.props.fiscalYears.map(fiscalYearObject=> fiscalYearObject.sessionFY),
			currentFiscalYear: this.props.currentFiscalYear,
            cobiId: 1,
            tab: 'overview',
			issueKey: undefined,
			issueText: false,
			draft: false,
		};

		const urlParams = parseUrl(url, defaultValues);
		const currentYear = this.props.currentYear;

		dispatchUpdateData([
			{path: 'app', field: 'currentYear', value: urlParams.year},
			{path: 'app', field: 'currentCobiId', value: urlParams.cobiId},
			{path: 'app', field: 'currentTab', value: urlParams.tab},
			{path: 'app', field: 'currentFund', value: undefined},
			{path: 'app', field: 'currentIssueKey', value: urlParams.issueKey},
			{path: 'app', field: 'issueText', value: urlParams.issueText},
			{path: 'app', field: 'draft', value: urlParams.draft},
			{path: 'app', field: 'admin', value: false},
		]);
		// get the menu based on the Year and cobiId
		// only fetch data if cobiId or year has changed
		if (!prevProps || this.props.currentCobiId !== prevProps.currentCobiId || currentYear !== prevProps.currentYear) {
			isAllowed() ? this.getAdminMenu(urlParams) : this.getMenu(urlParams);
		}

		if (urlParams.redirect && !(location.pathname.startsWith('/login') || location.pathname.startsWith('/silent_renew'))) {
			this.props.history.push(`/${urlParams.year}/${urlParams.cobiId}/${urlParams.tab}`);
		}
	}

	getMenu(urlParams) {
		webservice.public.navigation.menuById(urlParams.cobiId, urlParams.year)
			.then(value => {
				const menuMap = mapMenu(value);
				const defaultTitle = urlParams.cobiId === 1 ? location.pathname.includes('landing') ? 'Changes in COBI' : `COBI ${formatCobiYear(urlParams.year)}` : undefined;
				const pageTitle = (menuMap[urlParams.cobiId]) ? menuMap[urlParams.cobiId].orgName : defaultTitle;
				dispatchUpdateData([
					{path: 'app', field: 'currentPageTitle', value: pageTitle},
					{path: 'menu', field: 'menuObj', value},
					{path: 'menu', field: 'menuMap', value: menuMap}
				]);
			})
			.catch(reason => console.log(reason));
	}

	getAdminMenu(urlParams) {
		webservice.admin.navigation.menuById(urlParams.cobiId, urlParams.year)
			.then(value => {
				const menuMap = mapMenu(value);
				const defaultTitle = urlParams.cobiId === 1 ? `COBI ${formatCobiYear(urlParams.year)}` : undefined;
				const pageTitle = (menuMap[urlParams.cobiId]) ? menuMap[urlParams.cobiId].orgName : defaultTitle;
				dispatchUpdateData([
					{path: 'app', field: 'currentPageTitle', value: pageTitle},
					{path: 'menu', field: 'menuObj', value},
					{path: 'menu', field: 'menuMap', value: menuMap}
				]);
			})
			.catch(reason => console.log(reason));
	}

	render() {
		return (
			<Switch >
				<Route exact strict path="/login/callback" render={props => <LoginCallback {...Object.assign({}, this.props, props)} />}/>
				<Route exact strict path="/fund/:fundId" render={() => {
					return <Fund history={this.props.history} />;
				}}/>
				<Route path="/admin" render={() => (
					isAllowed() ?
						<AdminRoutes history={this.props.history}/>
						: <Redirect to="/"/>
				)}/>
				<Route path="/" render={() => (
					this.props.currentYear === undefined ?
						<WebserviceDown history={this.props.history} />
						: <Home history={this.props.history} />
				)}/>
			</Switch>
		);
	}
}

AppRoutes.propTypes = propTypes;
AppRoutes.defaultProps = defaultProps;

export default connect(mapStateToProps)(AppRoutes);
